import { clientV3SDK, metadataOnlyQuery, queryClient } from "@istari-frontend/api-client";
import { CreatableResourceType } from "@istari-frontend/resource/model";
import { type MutateOptions, useMutation } from "@tanstack/react-query";

import { fromResourceType } from "~/resource/data-source/adapter/v3/from-resource-type";
import { resourceQueryKeys } from "~/resource/data-source/api/resource-query-options";

import {
  assertUploadSessionCurrent,
  awaitUploadSessionReady,
  captureUploadSession,
  isUploadSessionCurrent,
  type UploadSession,
  uploadSessionCallbacks,
} from "./upload-session-callbacks";

type CreateResourceFromRevisionParams = {
  resourceType: CreatableResourceType;
  name: string;
  extension: string | null | undefined;
  size: number | null | undefined;
  mime: string | null | undefined;
  displayName: string;
  description?: string | null;
  contentToken: { sha: string; salt: string };
  propertiesToken: { sha: string; salt: string };
};
type Invocation = { variables: CreateResourceFromRevisionParams; session: UploadSession };
type CreatedResource = Awaited<ReturnType<typeof clientV3SDK.createResource>>;
type Callbacks = MutateOptions<CreatedResource, Error, CreateResourceFromRevisionParams>;

export function useCreateResourceFromRevision() {
  const mutation = useMutation({
    mutationFn: async ({ variables: params, session }: Invocation) => {
      await awaitUploadSessionReady(session);
      const resource = await clientV3SDK
        .createResource({
          resourceCreateDto: {
            resourceType: fromResourceType(params.resourceType),
            name: params.name,
            extension: params.extension ?? null,
            size: params.size ?? null,
            mime: params.mime ?? null,
            displayName: params.displayName,
            description: params.description ?? null,
            externalIdentifier: null,
            versionName: null,
            upstreamRemoteInfo: null,
            contentToken: params.contentToken,
            propertiesToken: params.propertiesToken,
          },
        })
        .catch((error: unknown) => {
          assertUploadSessionCurrent(session);
          throw error;
        });
      assertUploadSessionCurrent(session);
      return resource;
    },
    retry: false,
    onSuccess: (_data, { session }) => {
      if (!isUploadSessionCurrent(session)) return;
      void queryClient.invalidateQueries({ queryKey: resourceQueryKeys.all(), predicate: metadataOnlyQuery });
    },
    meta: { triggerGlobalInvalidation: false },
  });
  const callbacksForInvocation = (session: UploadSession, callbacks?: Callbacks) => {
    const guarded = uploadSessionCallbacks(callbacks, session);
    return {
      onSuccess: (data: CreatedResource, { variables }: Invocation, context: unknown) =>
        guarded.onSuccess?.(data, variables, context),
      onError: (error: Error, { variables }: Invocation, context: unknown) =>
        guarded.onError?.(error, variables, context),
      onSettled: (
        data: CreatedResource | undefined,
        error: Error | null,
        { variables }: Invocation,
        context: unknown,
      ) => guarded.onSettled?.(data, error, variables, context),
    };
  };
  return {
    ...mutation,
    variables: mutation.variables?.variables,
    mutate: (variables: CreateResourceFromRevisionParams, callbacks?: Callbacks) => {
      const session = captureUploadSession();
      mutation.mutate({ variables, session }, callbacksForInvocation(session, callbacks));
    },
    mutateAsync: (variables: CreateResourceFromRevisionParams, callbacks?: Callbacks) => {
      const session = captureUploadSession();
      return mutation.mutateAsync({ variables, session }, callbacksForInvocation(session, callbacks));
    },
  };
}
